// src/services/calendarVersionService.js
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';
import allotmentService from './allotmentService';

class CalendarVersionService {
  constructor() {
    this.versionsCollection = 'calendarVersions';
    this.allowancesCollection = 'dailyAllotments';
  }

  // Test method to check Firestore access
  async testFirestoreAccess() {
    try {
      console.log('🧪 Testing Firestore access to calendarVersions collection...');
      
      // Try to read from the collection
      const testQuery = query(collection(db, this.versionsCollection), limit(1));
      const querySnapshot = await getDocs(testQuery);
      
      console.log('✅ Successfully accessed calendarVersions collection');
      console.log(`📊 Collection has ${querySnapshot.size} documents`);
      
      return { success: true, count: querySnapshot.size };
    } catch (error) {
      console.error('❌ Failed to access calendarVersions collection:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      if (error.code === 'permission-denied') {
        console.error('🚫 This is likely a Firestore security rules issue. You need to add rules for the calendarVersions collection.');
      }
      
      return { success: false, error: error.message, code: error.code };
    }
  }

  // Save current calendar state as a new version
  async saveCalendarVersion(routeId, startDate, endDate, versionName, createdBy, notes = '') {
    try {
      console.log('💾 Saving calendar version:', { routeId, versionName, startDate, endDate });
      
      // Get current allowances for the date range
      const allowances = await allotmentService.getAllowancesForDateRange(routeId, startDate, endDate);
      console.log(`📅 Found ${allowances.length} allowances to save:`, allowances);
      
      // Create version document
      const versionData = {
        routeId,
        versionName,
        startDate,
        endDate,
        createdBy,
        createdAt: serverTimestamp(),
        notes,
        allowanceCount: allowances.length,
        status: 'active', // 'active', 'archived', 'draft'
        isDefault: false
      };

      console.log('📄 Version data to save:', versionData);

      // Save version metadata
      const versionRef = await addDoc(collection(db, this.versionsCollection), versionData);
      const versionId = versionRef.id;
      
      console.log('✅ Version document created with ID:', versionId);

      // Save snapshot of allowance data
      const batch = writeBatch(db);
      allowances.forEach(allowance => {
        const snapshotRef = doc(collection(db, this.versionsCollection, versionId, 'allowanceSnapshot'));
        batch.set(snapshotRef, {
          date: allowance.date,
          dailyAllowance: allowance.dailyAllowance || 0,
          baseCurrency: allowance.baseCurrency || 'USD',
          isLocked: allowance.isLocked || false,
          originalExists: allowance.exists || false
        });
      });

      await batch.commit();

      return {
        id: versionId,
        ...versionData,
        allowances: allowances.length
      };
    } catch (error) {
      console.error('Error saving calendar version:', error);
      throw error;
    }
  }

  // Get all versions for a route
  async getCalendarVersions(routeId) {
    try {
      console.log('🔍 Fetching calendar versions for routeId:', routeId);
      
      const q = query(
        collection(db, this.versionsCollection),
        where('routeId', '==', routeId)
        // orderBy('createdAt', 'desc') - Temporarily removed to avoid index requirement
      );

      const querySnapshot = await getDocs(q);
      const versions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
      })).sort((a, b) => {
        // Sort by createdAt descending (newest first)
        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return dateB.getTime() - dateA.getTime();
      });
      
      console.log(`📋 Found ${versions.length} calendar versions:`, versions);
      return versions;
    } catch (error) {
      console.error('❌ Error getting calendar versions:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        routeId,
        collection: this.versionsCollection
      });
      throw error;
    }
  }

  // Get specific version details with allowance data
  async getCalendarVersion(versionId) {
    try {
      const versionDoc = await getDoc(doc(db, this.versionsCollection, versionId));
      
      if (!versionDoc.exists()) {
        throw new Error('Version not found');
      }

      const versionData = {
        id: versionDoc.id,
        ...versionDoc.data(),
        createdAt: versionDoc.data().createdAt?.toDate?.() || versionDoc.data().createdAt
      };

      // Get allowance snapshot data
      const snapshotQuery = query(
        collection(db, this.versionsCollection, versionId, 'allowanceSnapshot'),
        orderBy('date')
      );

      const snapshotSnapshot = await getDocs(snapshotQuery);
      const allowances = snapshotSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      return {
        ...versionData,
        allowances
      };
    } catch (error) {
      console.error('Error getting calendar version:', error);
      throw error;
    }
  }

  // Load a version back to the calendar (apply to current allowances)
  async loadCalendarVersion(versionId, adminUser) {
    try {
      const version = await this.getCalendarVersion(versionId);
      
      if (!version.allowances || version.allowances.length === 0) {
        throw new Error('No allowance data in this version');
      }

      // Apply each allowance from the version
      let appliedCount = 0;
      for (const allowance of version.allowances) {
        try {
          const allowanceData = {
            dailyAllowance: allowance.dailyAllowance,
            baseCurrency: allowance.baseCurrency,
            isLocked: allowance.isLocked,
            reason: `Loaded from version: ${version.versionName}`
          };

          await allotmentService.setAllowance(
            version.routeId,
            allowance.date,
            allowanceData,
            adminUser
          );
          
          appliedCount++;
        } catch (allowanceError) {
          console.error(`Error applying allowance for ${allowance.date}:`, allowanceError);
        }
      }

      return {
        versionId,
        versionName: version.versionName,
        appliedCount,
        totalCount: version.allowances.length,
        routeId: version.routeId
      };
    } catch (error) {
      console.error('Error loading calendar version:', error);
      throw error;
    }
  }

  // Set version as default for a route
  async setDefaultVersion(routeId, versionId, adminUser) {
    try {
      const batch = writeBatch(db);

      // Remove default flag from all other versions for this route
      const existingVersions = await this.getCalendarVersions(routeId);
      existingVersions.forEach(version => {
        if (version.isDefault) {
          const versionRef = doc(db, this.versionsCollection, version.id);
          batch.update(versionRef, { isDefault: false });
        }
      });

      // Set the new default version
      const versionRef = doc(db, this.versionsCollection, versionId);
      batch.update(versionRef, { 
        isDefault: true,
        lastUsedAt: serverTimestamp(),
        lastUsedBy: adminUser
      });

      await batch.commit();

      return { success: true, versionId, routeId };
    } catch (error) {
      console.error('Error setting default version:', error);
      throw error;
    }
  }

  // Archive a version (soft delete)
  async archiveVersion(versionId, adminUser) {
    try {
      const versionRef = doc(db, this.versionsCollection, versionId);
      await updateDoc(versionRef, {
        status: 'archived',
        archivedAt: serverTimestamp(),
        archivedBy: adminUser
      });

      return { success: true, versionId };
    } catch (error) {
      console.error('Error archiving version:', error);
      throw error;
    }
  }

  // Get default version for a route (for pricing calculations)
  async getDefaultVersion(routeId) {
    try {
      const q = query(
        collection(db, this.versionsCollection),
        where('routeId', '==', routeId),
        where('isDefault', '==', true),
        where('status', '==', 'active'),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }

      const versionDoc = querySnapshot.docs[0];
      return {
        id: versionDoc.id,
        ...versionDoc.data(),
        createdAt: versionDoc.data().createdAt?.toDate?.() || versionDoc.data().createdAt
      };
    } catch (error) {
      console.error('Error getting default version:', error);
      return null;
    }
  }

  // Compare two versions and return differences
  async compareVersions(versionId1, versionId2) {
    try {
      const [version1, version2] = await Promise.all([
        this.getCalendarVersion(versionId1),
        this.getCalendarVersion(versionId2)
      ]);

      const allowances1Map = {};
      version1.allowances.forEach(a => {
        allowances1Map[a.date] = a;
      });

      const allowances2Map = {};
      version2.allowances.forEach(a => {
        allowances2Map[a.date] = a;
      });

      const differences = [];
      const allDates = new Set([
        ...Object.keys(allowances1Map),
        ...Object.keys(allowances2Map)
      ]);

      allDates.forEach(date => {
        const allowance1 = allowances1Map[date];
        const allowance2 = allowances2Map[date];

        if (!allowance1) {
          differences.push({
            date,
            type: 'added',
            version2: allowance2
          });
        } else if (!allowance2) {
          differences.push({
            date,
            type: 'removed',
            version1: allowance1
          });
        } else if (
          allowance1.dailyAllowance !== allowance2.dailyAllowance ||
          allowance1.isLocked !== allowance2.isLocked
        ) {
          differences.push({
            date,
            type: 'modified',
            version1: allowance1,
            version2: allowance2
          });
        }
      });

      return {
        version1: {
          id: version1.id,
          name: version1.versionName,
          createdAt: version1.createdAt
        },
        version2: {
          id: version2.id,
          name: version2.versionName,
          createdAt: version2.createdAt
        },
        differences: differences.sort((a, b) => a.date.localeCompare(b.date))
      };
    } catch (error) {
      console.error('Error comparing versions:', error);
      throw error;
    }
  }
}

const calendarVersionService = new CalendarVersionService();
export default calendarVersionService;